import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  PasswordInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { platformApi, type PlatformUser } from './platformApi';

export function PlatformUsersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const { data: users, isLoading } = useQuery({
    queryKey: ['platform-users'] as const,
    queryFn: async () => (await platformApi.get<PlatformUser[]>('/api/platform/users')).data,
  });

  const toggleActive = useMutation({
    mutationFn: async (user: PlatformUser) =>
      platformApi.put(`/api/platform/users/${user.platformUserId}`, {
        fullNameAr: user.fullNameAr,
        fullNameEn: user.fullNameEn,
        role: user.role,
        isActive: !user.isActive,
        newPassword: null,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['platform-users'] }),
    onError: () =>
      notifications.show({ color: 'red', title: t('platform.users.updateFailed'), message: '' }),
  });

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600} size="lg">
          {t('platform.users.title')}
        </Text>
        <Button
          color="wheels"
          leftSection={<IconPlus size={16} stroke={1.8} />}
          onClick={() => setCreateOpen(true)}
        >
          {t('platform.users.add')}
        </Button>
      </Group>

      <Paper p="md">
        {isLoading ? (
          <Group justify="center" py="lg">
            <Loader color="wheelsRed" />
          </Group>
        ) : (
          <Table verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('platform.users.name')}</Table.Th>
                <Table.Th>{t('platform.users.email')}</Table.Th>
                <Table.Th>{t('platform.users.role')}</Table.Th>
                <Table.Th>{t('platform.users.active')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(users ?? []).map((user) => (
                <Table.Tr key={user.platformUserId}>
                  <Table.Td>{user.fullNameAr}</Table.Td>
                  <Table.Td dir="ltr">{user.email}</Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={user.role === 1 ? 'wheels' : 'gray'}>
                      {user.role === 1 ? t('platform.roles.super') : t('platform.roles.sub')}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Switch
                      checked={user.isActive}
                      onChange={() => toggleActive.mutate(user)}
                      color="green"
                    />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <CreateUserModal opened={createOpen} onClose={() => setCreateOpen(false)} />
    </Stack>
  );
}

function CreateUserModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ email: '', password: '', fullNameAr: '', role: '2' });

  const mutation = useMutation({
    mutationFn: async () =>
      platformApi.post('/api/platform/users', {
        email: form.email,
        password: form.password,
        fullNameAr: form.fullNameAr,
        fullNameEn: null,
        role: Number(form.role),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform-users'] });
      setForm({ email: '', password: '', fullNameAr: '', role: '2' });
      onClose();
    },
    onError: () =>
      notifications.show({ color: 'red', title: t('platform.users.updateFailed'), message: '' }),
  });

  return (
    <Modal opened={opened} onClose={onClose} title={t('platform.users.add')}>
      <Stack gap="sm">
        <TextInput
          label={t('platform.users.name')}
          value={form.fullNameAr}
          onChange={(e) => setForm((f) => ({ ...f, fullNameAr: e.currentTarget.value }))}
          required
        />
        <TextInput
          label={t('platform.users.email')}
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.currentTarget.value }))}
          dir="ltr"
          required
        />
        <PasswordInput
          label={t('platform.login.password')}
          description={t('platform.tenants.ownerPasswordHint')}
          value={form.password}
          onChange={(e) => setForm((f) => ({ ...f, password: e.currentTarget.value }))}
          required
        />
        <Select
          label={t('platform.users.role')}
          value={form.role}
          onChange={(v) => setForm((f) => ({ ...f, role: v ?? '2' }))}
          data={[
            { value: '1', label: t('platform.roles.super') },
            { value: '2', label: t('platform.roles.sub') },
          ]}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            color="wheels"
            loading={mutation.isPending}
            disabled={!form.email || form.password.length < 10 || !form.fullNameAr}
            onClick={() => mutation.mutate()}
          >
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
